/**
 * SB6-ANA-CACHE-02 — single mount point for the prefetcher.
 *
 * `usePrefetch` owns side effects (binding the scheduler to the acquisition,
 * installing the SAS re-mint hook, recomputing the P0/P1 window on every
 * activation + filter change). It must be mounted EXACTLY ONCE per analysis
 * surface — two instances would double the effect work (idempotent, but
 * wasteful). This provider mounts it once and shares the resulting controls
 * (`downloadDataset` / `cancelDataset` / `progress` / `saveDataHint`) with the
 * "Download dataset" button in the dataset header via context.
 *
 * The automatic window prefetch runs whenever this provider is mounted —
 * independent of whether the header (and thus the button) is rendered (e.g. it
 * is suppressed in a pop-out window, which shares the origin-wide cache and
 * lets the main window drive the warm).
 */
import { createContext, useContext } from "react";
import { usePrefetch, type UsePrefetchResult } from "./usePrefetch.js";

const PrefetchContext = createContext<UsePrefetchResult | null>(null);

export function PrefetchProvider({
  dataAcquisitionId,
  datasetName,
  children,
}: {
  dataAcquisitionId: string;
  /**
   * SB6-ANA-CACHE-03 — best-effort human label for the storage UI / Data
   * Acquisitions list rows (e.g. "Asset · 22340"). Optional; the cache works
   * without it (rows then fall back to the acquisition id).
   */
  datasetName?: string;
  children: React.ReactNode;
}): React.ReactElement {
  const value = usePrefetch(dataAcquisitionId, datasetName);
  return (
    <PrefetchContext.Provider value={value}>
      {children}
    </PrefetchContext.Provider>
  );
}

/**
 * Read the shared prefetch controls. Returns `null` outside a provider so a
 * stray consumer degrades gracefully rather than crashing (the button simply
 * doesn't render).
 */
export function usePrefetchControls(): UsePrefetchResult | null {
  return useContext(PrefetchContext);
}
